// @ts-nocheck
/* eslint-disable */
/* eslint-disable @typescript-eslint/no-unused-expressions */
import React from 'react';
import type { TrackingDataV2 } from '@oneaudi/audi-tracking-service';
import { FeatureServiceContext } from '../contexts/pbc-feature-services-context';
import packageInfo from '../../../package.json';
import type { DepartmentOpeningHours } from '../components/map/PartnerBusinessCard/PartnerData';
import type { ctaBarButtonConfigProps } from '../components/map/PartnerBusinessCard/pbc-types';
import trackingContext from '../contexts/tracking-context';

interface ITrackingManager {
  pageLoad: (variant: string, partnerID: string) => void;
  click: (
    action: string,
    targetURL: string,
    elementName: string,
    label: string,
    headline: string,
    pos: string,
    elementValue: string,
  ) => void;
  impression(
    variant: string,
    headline: string,
    partnerDepartments?: DepartmentOpeningHours[],
    ctas?: ctaBarButtonConfigProps[],
  ): void;
}
const { version: appVersion } = packageInfo;

const createAdditionalPageLoadData = (appVariant: string, partnerID: string) => ({
  componentUpdate: {
    componentInfo: {
      implementer: 2,
      version: appVersion,
      variant: appVariant,
    },
    dealer: {
      dealerInfo: {
        dealerID: partnerID,
      },
    },
  },
});

const createEventPayload = (
  action: string,
  name: string,
  attributes: Record<string, unknown>,
  additionalTrackingData?: Record<string, unknown>,
): TrackingDataV2 => {
  const defaultAttributes = {
    componentName: 'partner-business-card',
  };

  return {
    event: {
      attributes: {
        ...defaultAttributes,
        ...attributes,
      },
      eventInfo: {
        eventAction: action,
        eventName: name,
      },
    },
    ...additionalTrackingData,
  };
};

const getRelatedElements = (
  elementName: string,
  relatedElements: string[] = [],
): Array<Record<string, unknown>> =>
  relatedElements.map((element) => ({
    elementName,
    elementValue: element,
  }));

const getRelatedElementsForImpressions = (
  variant: string,
  departments: DepartmentOpeningHours[],
  ctas: ctaBarButtonConfigProps[],
) => {
  const relEl: Array<Record<string, unknown>> = [];

  (variant === 'full-compact' || variant === 'full-long' || variant === 'fixed-narrow') &&
    relEl.push(
      ...getRelatedElements('cta', [...ctas.map((button) => button.label)]),
      ...getRelatedElements('link', [
        'phone',
        'location',
        'mail',
        'contact data download',
        'pws link',
      ]),
      ...getRelatedElements('headline', ['pws link']),
    );

  variant === 'full-compact' &&
    relEl.push(...getRelatedElements('tab', ['address & contact', 'opening hours']));

  variant === 'fixed-narrow' &&
    relEl.push(
      ...getRelatedElements('accordion', [
        ...(departments ? departments.map((department) => department.departmentName) : []),
      ]),
    );

  variant === 'tag' &&
    relEl.push({
      elementName: 'link',
      elementValue: 'location',
    });

  return relEl;
};

export function useTrackingManager(): ITrackingManager {
  const { logger } = React.useContext(FeatureServiceContext);
  const { trackingService } = trackingContext.useTrackingState();

  return React.useMemo((): ITrackingManager => {
    function track(...args: Parameters<typeof createEventPayload>): void {
      if (trackingService) {
        const payload = createEventPayload(...args);
        trackingService.track(payload);
      }
    }
    return {
      pageLoad(variant, partnerID) {
        track(
          'feature_app_ready',
          'partner business card - feature app ready',
          {
            label: '',
            targetURL: '',
            clickID: '',
            elementName: '',
            value: '',
            pos: '',
          },
          createAdditionalPageLoadData(variant, partnerID),
        );
      },

      click(action, targetURL, elementName, label, headline, pos, elementValue) {
        const name = elementName === 'link' ? 'contact links' : elementName;
        const partnerAction = action === 'content' && elementName === 'cta' ? label : '';
        const partnerInclude = label.includes('Set') ? 'set dealer' : 'change dealer';

        partnerAction
          ? track(action, `partner business card - click on ${partnerInclude}`, {
              label,
              elementName,
              targetURL,
              clickID: '',
              value: headline,
              pos,
              relatedElements: [{ elementName, elementValue }],
            })
          : track(action, `partner business card - click on ${name}`, {
              label,
              elementName,
              targetURL,
              clickID: '',
              value: headline,
              pos,
              relatedElements: [{ elementName, elementValue }],
            });
      },

      impression(
        variant,
        headline,
        departments: DepartmentOpeningHours[],
        ctas: ctaBarButtonConfigProps[],
      ) {
        track('impression', 'partner business card - impression', {
          label: '',
          targetURL: '',
          clickID: '',
          elementName: '',
          value: headline,
          pos: '',
          relatedElements: getRelatedElementsForImpressions(variant, departments || [], ctas || []),
        });
      },
    };
  }, [logger, trackingService]);
}
